import React, { useRef, useState } from 'react';
import { Button, Card, Stack, Flex, useTheme, spacing, fontFamily } from '@fti/design-system';

const ACCEPTED_EXTENSIONS = ['.txt', '.docx', '.pdf'] as const;

type Tab = 'paste' | 'upload';

export interface UploadPanelProps {
  onSubmit: (input: { text?: string; file?: File; titleHint?: string }) => void;
  onCancel: () => void;
  busy: boolean;
  disabled?: boolean;
}

function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function fileExt(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const UploadPanel: React.FC<UploadPanelProps> = ({
  onSubmit,
  onCancel,
  busy,
  disabled,
}) => {
  const { theme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<Tab>('paste');
  const [text, setText] = useState('');
  const [titleHint, setTitleHint] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const wordCount = countWords(text);

  const handleFile = (f: File | null | undefined) => {
    setValidationError(null);
    if (!f) {
      setFile(null);
      return;
    }
    const ext = fileExt(f.name);
    if (!ACCEPTED_EXTENSIONS.includes(ext as typeof ACCEPTED_EXTENSIONS[number])) {
      setValidationError(
        `Unsupported file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`,
      );
      setFile(null);
      return;
    }
    setFile(f);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const canSubmit =
    !busy &&
    !disabled &&
    !validationError &&
    ((tab === 'paste' && wordCount > 0) || (tab === 'upload' && file !== null));

  const handleSubmit = () => {
    setValidationError(null);
    if (tab === 'paste') {
      onSubmit({ text, titleHint: titleHint || undefined });
    } else if (file) {
      onSubmit({ file, titleHint: titleHint || undefined });
    }
  };

  const tabButton = (active: boolean): React.CSSProperties => ({
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: '0.9rem',
    fontWeight: 600,
    fontFamily: fontFamily.sans,
    color: active ? theme.colors.primary[700] : theme.colors.text.secondary,
    background: active ? theme.colors.primary[50] : 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  const inputBase: React.CSSProperties = {
    width: '100%',
    padding: `${spacing.md} ${spacing.md}`,
    fontFamily: fontFamily.sans,
    fontSize: '0.95rem',
    lineHeight: 1.55,
    borderRadius: '10px',
    border: `1px solid ${theme.colors.border.default}`,
    background: '#FFFFFF',
    color: theme.colors.text.primary,
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
  };

  return (
    <Card
      variant="elevated"
      style={{
        borderRadius: '16px',
        boxShadow: '0 12px 40px -16px rgba(0, 84, 163, 0.15), 0 2px 8px rgba(15, 23, 42, 0.04)',
        border: `1px solid ${theme.colors.primary[100]}`,
      }}
    >
      <Card.Body style={{ padding: spacing.xl }}>
        <Stack gap="lg">
          {/* Tab switcher */}
          <Flex
            gap="xs"
            style={{
              padding: spacing.xs,
              background: theme.colors.background.paper,
              borderRadius: '10px',
              alignSelf: 'flex-start',
            }}
          >
            <button type="button" onClick={() => setTab('paste')} style={tabButton(tab === 'paste')}>
              Paste text
            </button>
            <button
              type="button"
              onClick={() => setTab('upload')}
              style={tabButton(tab === 'upload')}
            >
              Upload file
            </button>
          </Flex>

          {tab === 'paste' && (
            <Stack gap="sm">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste a speech, interview transcript, press release, or meeting notes…"
                disabled={busy}
                rows={11}
                style={{ ...inputBase, resize: 'vertical', minHeight: '220px' }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.primary[500];
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.colors.primary[100]}`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border.default;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <Flex justify="between" align="center">
                <span style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                  {wordCount === 0
                    ? 'Paste any speech, transcript, or release.'
                    : `${wordCount.toLocaleString()} word${wordCount === 1 ? '' : 's'}`}
                </span>
              </Flex>
            </Stack>
          )}

          {tab === 'upload' && (
            <Stack gap="sm">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(',')}
                onChange={handleFileChange}
                disabled={busy}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                disabled={busy}
                style={{
                  padding: `${spacing.xl} ${spacing.lg}`,
                  background: dragActive ? theme.colors.primary[50] : '#FFFFFF',
                  border: `2px dashed ${
                    dragActive ? theme.colors.primary[500] : theme.colors.border.default
                  }`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  textAlign: 'center',
                  fontFamily: fontFamily.sans,
                  color: theme.colors.text.primary,
                }}
              >
                <div
                  style={{
                    fontSize: '2rem',
                    color: theme.colors.primary[500],
                    marginBottom: spacing.xs,
                  }}
                >
                  ↑
                </div>
                {file ? (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{file.name}</div>
                    <div style={{ fontSize: '0.8rem', color: theme.colors.text.secondary, marginTop: 4 }}>
                      {humanSize(file.size)} · click to replace
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      Drop a file or click to browse
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: theme.colors.text.secondary,
                        marginTop: 4,
                      }}
                    >
                      Accepts {ACCEPTED_EXTENSIONS.join(', ')}
                    </div>
                  </div>
                )}
              </button>
            </Stack>
          )}

          <input
            type="text"
            value={titleHint}
            onChange={(e) => setTitleHint(e.target.value)}
            placeholder="Optional title (e.g., 'Q4 All-Hands Speech')"
            disabled={busy}
            maxLength={200}
            style={{ ...inputBase, fontSize: '0.9rem' }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = theme.colors.primary[500];
              e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.colors.primary[100]}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = theme.colors.border.default;
              e.currentTarget.style.boxShadow = 'none';
            }}
          />

          {validationError && (
            <div
              style={{
                fontSize: '0.875rem',
                color: theme.colors.semantic.error.default,
                background: `${theme.colors.semantic.error.light}15`,
                border: `1px solid ${theme.colors.semantic.error.light}`,
                borderRadius: '8px',
                padding: spacing.md,
              }}
            >
              {validationError}
            </div>
          )}

          <Flex gap="sm" align="center">
            <Button
              variant="solid"
              color="primary"
              size="md"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {busy ? 'Extracting…' : 'Extract Quotes'}
            </Button>
            {busy && (
              <Button variant="outline" color="error" size="md" onClick={onCancel}>
                Cancel
              </Button>
            )}
            {busy && (
              <span
                style={{
                  fontSize: '0.85rem',
                  color: theme.colors.text.secondary,
                }}
              >
                Analyzing — this usually takes 5–15 seconds.
              </span>
            )}
          </Flex>
        </Stack>
      </Card.Body>
    </Card>
  );
};
